import React, { useState, useEffect, useRef } from 'react';
import { Button, DataTable } from '../../../../../ui';
import { getColumns } from './Functions/GetColumns';
import { sortCandidates } from "../../../../../utils/types/JobOpenings";
import inputStyles from "../../../../../ui/input/input.module.css";
import { SearchIcons } from "../../../../../icons/icons";
import { CustomSelect } from "../../../../../ui/custom_react_select/custom_select";
import { SelectOptions } from "../../../../../utils/types/react_select_types";
import { SearchIcon } from "../../../../../ui/icons/icons";
import { getPsychometricApplicants, updateJobApplicantStatus } from "../../../../../utils/api_service/JobOpenings";
import { useParams } from "react-router-dom";

export interface CandidateListType {
    name: string;
    email: string;
    id: string;
    psychometricStatus: string;
    applicantStatus: string;
    invited: boolean;
}

// ---------- Debounce hook (must be outside component) ----------

function useDebounce<T>(value: T, delay: number): T {
    const [debounced, setDebounced] = useState<T>(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

// ---------- Minimal dependency-free XLSX writer (stored/uncompressed ZIP) ----------

const crc32Table = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) {
            c = c & 1 ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
        }
        table[n] = c;
    }
    return table;
})();

function crc32(bytes: Uint8Array): number {
    let crc = 0xffffffff;
    for (let i = 0; i < bytes.length; i++) {
        crc = crc32Table[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
}

function strToBytes(str: string): Uint8Array {
    return new TextEncoder().encode(str);
}

function dosDateTime(date: Date) {
    const time =
        ((date.getHours() & 0x1f) << 11) |
        ((date.getMinutes() & 0x3f) << 5) |
        ((Math.floor(date.getSeconds() / 2)) & 0x1f);
    const dosDate =
        (((date.getFullYear() - 1980) & 0x7f) << 9) |
        (((date.getMonth() + 1) & 0x0f) << 5) |
        (date.getDate() & 0x1f);
    return { time, dosDate };
}

interface ZipEntry {
    name: string;
    data: Uint8Array;
}

function buildZip(files: { name: string; content: string }[]): Blob {
    const now = new Date();
    const { time, dosDate } = dosDateTime(now);
    const entries: ZipEntry[] = files.map((f) => ({ name: f.name, data: strToBytes(f.content) }));

    const localParts: Uint8Array[] = [];
    const centralParts: Uint8Array[] = [];
    let offset = 0;

    for (const entry of entries) {
        const nameBytes = strToBytes(entry.name);
        const crc = crc32(entry.data);
        const size = entry.data.length;

        const localHeader = new DataView(new ArrayBuffer(30));
        localHeader.setUint32(0, 0x04034b50, true);
        localHeader.setUint16(4, 20, true);
        localHeader.setUint16(6, 0, true);
        localHeader.setUint16(8, 0, true);
        localHeader.setUint16(10, time, true);
        localHeader.setUint16(12, dosDate, true);
        localHeader.setUint32(14, crc, true);
        localHeader.setUint32(18, size, true);
        localHeader.setUint32(22, size, true);
        localHeader.setUint16(26, nameBytes.length, true);
        localHeader.setUint16(28, 0, true);

        const localChunk = new Uint8Array(30 + nameBytes.length + entry.data.length);
        localChunk.set(new Uint8Array(localHeader.buffer), 0);
        localChunk.set(nameBytes, 30);
        localChunk.set(entry.data, 30 + nameBytes.length);
        localParts.push(localChunk);

        const centralHeader = new DataView(new ArrayBuffer(46));
        centralHeader.setUint32(0, 0x02014b50, true);
        centralHeader.setUint16(4, 20, true);
        centralHeader.setUint16(6, 20, true);
        centralHeader.setUint16(8, 0, true);
        centralHeader.setUint16(10, 0, true);
        centralHeader.setUint16(12, time, true);
        centralHeader.setUint16(14, dosDate, true);
        centralHeader.setUint32(16, crc, true);
        centralHeader.setUint32(20, size, true);
        centralHeader.setUint32(24, size, true);
        centralHeader.setUint16(28, nameBytes.length, true);
        centralHeader.setUint16(30, 0, true);
        centralHeader.setUint16(32, 0, true);
        centralHeader.setUint16(34, 0, true);
        centralHeader.setUint16(36, 0, true);
        centralHeader.setUint32(38, 0, true);
        centralHeader.setUint32(42, offset, true);

        const centralChunk = new Uint8Array(46 + nameBytes.length);
        centralChunk.set(new Uint8Array(centralHeader.buffer), 0);
        centralChunk.set(nameBytes, 46);
        centralParts.push(centralChunk);

        offset += localChunk.length;
    }

    const centralDirSize = centralParts.reduce((sum, p) => sum + p.length, 0);
    const centralDirOffset = offset;

    const endRecord = new DataView(new ArrayBuffer(22));
    endRecord.setUint32(0, 0x06054b50, true);
    endRecord.setUint16(4, 0, true);
    endRecord.setUint16(6, 0, true);
    endRecord.setUint16(8, entries.length, true);
    endRecord.setUint16(10, entries.length, true);
    endRecord.setUint32(12, centralDirSize, true);
    endRecord.setUint32(16, centralDirOffset, true);
    endRecord.setUint16(20, 0, true);

    const allParts = [...localParts, ...centralParts, new Uint8Array(endRecord.buffer)];
    return new Blob(allParts, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

function escapeXml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

function colName(index: number): string {
    let name = '';
    let n = index + 1;
    while (n > 0) {
        const rem = (n - 1) % 26;
        name = String.fromCharCode(65 + rem) + name;
        n = Math.floor((n - 1) / 26);
    }
    return name;
}

function buildSheetXml(rows: (string | number)[][]): string {
    const rowXml = rows.map((row, rIdx) => {
        const cells = row.map((cell, cIdx) => {
            const ref = `${colName(cIdx)}${rIdx + 1}`;
            if (typeof cell === 'number' && !isNaN(cell)) {
                return `<c r="${ref}"><v>${cell}</v></c>`;
            }
            const text = escapeXml(String(cell ?? ''));
            return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${text}</t></is></c>`;
        }).join('');
        return `<row r="${rIdx + 1}">${cells}</row>`;
    }).join('');

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetData>${rowXml}</sheetData>
</worksheet>`;
}

function createXlsxBlob(rows: (string | number)[][]): Blob {
    const sheetXml = buildSheetXml(rows);

    const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`;

    const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

    const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="Candidates" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;

    const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
</Relationships>`;

    return buildZip([
        { name: '[Content_Types].xml', content: contentTypes },
        { name: '_rels/.rels', content: rootRels },
        { name: 'xl/workbook.xml', content: workbookXml },
        { name: 'xl/_rels/workbook.xml.rels', content: workbookRels },
        { name: 'xl/worksheets/sheet1.xml', content: sheetXml },
    ]);
}

// ---------- Component ----------

export const Config: React.FC = () => {

    const [loading, setLoading] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [candidatesList, setCandidatesList] = useState<CandidateListType[]>([]);
    const [sort, setSort] = useState<sortCandidates>({ firstName: 'asc' });
    const [localName, setLocalName] = useState('');
    const [localEmail, setLocalEmail] = useState('');
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [jobLevel, setJobLevel] = useState<'Staff' | 'Manager'>('Staff');
    const exportMenuRef = useRef<HTMLDivElement>(null);
    const { id } = useParams<{ id: string }>();

    const debouncedName = useDebounce(localName, 400);
    const debouncedEmail = useDebounce(localEmail, 400);

    const jobLevelOptions: SelectOptions[] = [
        { value: 'Staff', label: 'Staff' },
        { value: 'Manager', label: 'Manager' },
    ];

    const onPageChange = (page: number) => {
        setPageNumber(page);
    };

    const handleRetake = (row: any) => {
        console.log('Remove invite', row);
    };

    // Reset to page 1 when search terms change
    useEffect(() => {
        setPageNumber(1);
    }, [debouncedName, debouncedEmail]);

    // Fetch when page or debounced search values change
    useEffect(() => {
        getApplicants();
    }, [debouncedName, debouncedEmail, pageNumber]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
                setShowExportMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getApplicants = async () => {
        setLoading(true);
        try {
            const response = await getPsychometricApplicants(
                id,
                pageNumber,
                30,
                debouncedName,
                debouncedEmail,
            );

            if (response.ok) {
                const data = await response.json();
                setCandidatesList(data.results ?? []);
                setTotalPages(Math.ceil(data.total_count / 30));
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    const jobLevelOptions2: SelectOptions[] = [
        { value: 'Outstanding', label: 'Outstanding' },
        { value: 'Suitable', label: 'Suitable' },
        { value: 'Auto-Assessment', label: 'Auto-Assessment' },
        { value: 'New', label: 'New' },
    ];

    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

    const handleSelectRow = (_rowIndex: number, row: CandidateListType) => {
        setSelectedRows((prev) => {
            const next = new Set(prev);
            if (next.has(row.id)) {
                next.delete(row.id);
            } else {
                next.add(row.id);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        if (selectedRows.size === candidatesList.length) {
            setSelectedRows(new Set());
        } else {
            setSelectedRows(new Set(candidatesList.map((c) => c.id)));
        }
    };

    const getExportRows = () => {
        const selected = candidatesList.filter((c) => selectedRows.has(c.id));
        const headers = ['ID', 'Email', 'Full Name', 'Job Level'];
        const rows = selected.map((c) => [
            c.id,
            c.email,
            c.name,
            jobLevel,
        ]);
        return { headers, rows };
    };

    const downloadBlob = (blob: Blob, filename: string) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const updateSelectedToPending = async () => {
        const selected = candidatesList.filter((c) => selectedRows.has(c.id));
        if (!selected.length) return;

        try {
            await updateJobApplicantStatus(id!, {
                jobApplicantIds: selected.map((c) => c.id),
                applicantStatus: 'Psychometric-Inprogress',
            });
            await getApplicants(); // refresh the table
        } catch (e) {
            console.error('Failed to update applicant statuses', e);
        }
    };

    const handleExportCSV = async () => {
        const { headers, rows } = getExportRows();
        if (!rows.length) return;

        const today = new Date().toISOString().split('T')[0];
        const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        downloadBlob(blob, `candidates_${today}.csv`);
        setShowExportMenu(false);
        await updateSelectedToPending();
    };

    const handleExportXLSX = async () => {
        const { headers, rows } = getExportRows();
        if (!rows.length) return;

        const today = new Date().toISOString().split('T')[0];
        const blob = createXlsxBlob([headers, ...rows]);
        downloadBlob(blob, `candidates_${today}.xlsx`);
        setShowExportMenu(false);
        await updateSelectedToPending();
    };

    const isRowSelected = (_rowIndex: number, row: CandidateListType) =>
        selectedRows.has(row.id);

    const isAllSelected = selectedRows.size === candidatesList.length;
    const isIndeterminate = selectedRows.size > 0 && selectedRows.size < candidatesList.length;

    const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
        setSort((prev) => {
            const next = { ...prev };
            if (next[key as keyof sortCandidates] === direction) {
                delete next[key as keyof sortCandidates];
            } else {
                next[key as keyof sortCandidates] = direction;
            }
            return next;
        });
        setPageNumber(1);
    };

    return (
        <div className='flex flex-col gap-4'>
            <div className='bg-slate-50/50 border border-slate-100 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3'>

                {/* Left: search + level toggle */}
                <div className='flex items-center gap-2 flex-wrap'>
                    <div className='relative'>
                        <div className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400'>
                            <SearchIcon className='w-3.5 h-3.5' />
                        </div>
                        <input
                            className={`${inputStyles.inputStyles} !mt-0 !pl-8 !h-9 !text-sm w-44`}
                            type='text'
                            placeholder='Search by name'
                            value={localName}
                            onChange={(e) => setLocalName(e.target.value)}
                        />
                    </div>

                    {console.log(candidatesList)}

                    <div className='relative'>
                        <div className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400'>
                            <SearchIcons className='w-3.5 h-3.5' />
                        </div>
                        <input
                            className={`${inputStyles.inputStyles} !mt-0 !pl-8 !h-9 !text-sm w-44`}
                            type='text'
                            placeholder='Search by email'
                            value={localEmail}
                            onChange={(e) => setLocalEmail(e.target.value)}
                        />
                    </div>

                    {/* Segmented job level toggle */}
                    <div className='flex items-center h-9 bg-white border border-slate-200 rounded-lg p-0.5 gap-0.5'>
                        {(['Staff', 'Manager'] as const).map((level) => (
                            <button
                                key={level}
                                onClick={() => setJobLevel(level)}
                                className={`h-7 px-3 rounded-md text-xs font-medium transition-all ${
                                    jobLevel === level
                                        ? 'bg-emerald-600 text-white shadow-sm'
                                        : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                {level}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Right: actions */}
                <div className='flex items-center gap-2'>
                    <Button
                        variant='primary'
                        className='h-9 !rounded-lg !text-sm'
                        label='Send invites'
                    />

                    <div className='relative' ref={exportMenuRef}>
                        <Button
                            variant='primary'
                            onClick={() => setShowExportMenu((prev) => !prev)}
                            className='h-9 !rounded-lg !text-sm'
                            label='Export ▾'
                        />
                        {showExportMenu && (
                            <div className='absolute top-[calc(100%+4px)] right-0 z-50 w-36 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden'>
                                <button
                                    onClick={handleExportCSV}
                                    className='w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors'
                                >
                                    Export as CSV
                                </button>
                                <button
                                    onClick={handleExportXLSX}
                                    className='w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100'
                                >
                                    Export as XLSX
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Job level hint */}
            <p className='text-xs text-slate-400 -mt-2 px-1'>
                Job level <span className='font-medium text-emerald-600'>{jobLevel}</span> will be included in exports.
            </p>

            {candidatesList.length ? (
                <DataTable
                    isLoading={loading}
                    columns={getColumns(handleRetake)}
                    data={candidatesList}
                    totalRecords={candidatesList.length}
                    rowsPerPage={10}
                    onPageChange={onPageChange}
                    currentPage={pageNumber}
                    totalPages={totalPages}
                    onSortChange={handleSortChange}
                    sortConfig={Object.entries(sort).map(([key, dir]) => ({
                        key,
                        direction: dir,
                    }))}
                    hasCheckBox
                    onClickRow={(row) => handleSelectRow(-1, row)}
                    onSelectAll={handleSelectAll}
                    onSelectRow={handleSelectRow}
                    isRowSelected={isRowSelected}
                    isAllSelected={isAllSelected}
                    isIndeterminate={isIndeterminate}
                />
            ) : (
                <div className='text-center py-10'>
                    <div className='text-gray-800 text-lg'>No Applicants</div>
                </div>
            )}
        </div>
    );
};